<template>
  <div class="radio-container def-radio">
    <fieldset class="radio-group" :id="groupId" :aria-describedby="inputError ? errorId : null">
      <legend class="radio-group-label">
        <template v-if="icon">
          <font-awesome-icon :icon="icon" :style="{ width: '16px', height: '16px', color: iconColor }" class="radio-group-icon" />
        </template>
        {{ label }}<span v-if="required" aria-hidden="true"> *</span>
      </legend>
      <label
        v-for="(option, index) in options"
        :key="option.value"
        :for="optionId(option, index)"
        class="radio-button"
        :class="{ 'disabled': option.disabled }"
      >
        <input
          :id="optionId(option, index)"
          type="radio"
          :name="groupName"
          :value="option.value"
          :checked="localValue === option.value"
          @change="onRadioChange(option.value)"
          :aria-invalid="inputError ? 'true' : null"
          :aria-describedby="inputError ? errorId : null"
          :required="required"
          :disabled="option.disabled"
        />
        <span class="checkmark" aria-hidden="true"></span>
        <span class="radio-button-label">{{ option.label }}</span>
      </label>
    </fieldset>
    <div v-if="inputError" :id="errorId" class="radio-error" role="alert" aria-live="assertive">
      {{ inputError }}
    </div>
  </div>
</template>

<script>
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';

export default {
  name: 'RadioButton',
  components: { FontAwesomeIcon },
  props: {
    value: [String, Number],
    label: { type: String, required: true },
    options: { type: Array, required: true }, // [{ value, label }]
    required: { type: Boolean, default: false },
    id: { type: String, default: null },
    error: { type: String, default: '' },
    icon: { type: [String, Array, Object], default: null },
    iconColor: { type: String, default: null },
  },
  data() {
    return {
      localValue: this.value,
      inputError: this.error,
    };
  },
  computed: {
    groupId() {
      return this.id || `radio-group-${this._uid}`;
    },
    groupName() {
      return this.groupId;
    },
    errorId() {
      return `${this.groupId}-error`;
    }
  },
  watch: {
    value(newVal) {
      this.localValue = newVal;
    },
    error(newErr) {
      this.inputError = newErr;
    }
  },
  methods: {
    optionId(option, index) {
      const value = String(option.value).replace(/[^A-Za-z0-9_-]/g, '-');
      return `${this.groupId}-${index}-${value || 'option'}`;
    },
    onRadioChange(val) {
      this.inputError = '';
      this.localValue = val;
      this.$emit('input', val);
    }
  },
};
</script>

<style scoped>
.radio-container {
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}
.radio-group {
  border: 0;
  min-inline-size: 0;
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 0.5em;
  margin-top: 0.5em;
}
.radio-error {
  margin-top: 0.25rem;
  text-align: left;
}

/* Structural radio layout; visual states are handled by .def-radio */
.radio-button {
  display: flex;
  align-items: center;
  position: relative;
  min-height: 24px;
  padding-left: 32px;
  cursor: pointer;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  user-select: none;
}
.radio-button input {
  position: absolute;
  opacity: 0;
  cursor: pointer;
  top: 50%;
  left: 0;
  width: 24px;
  height: 24px;
  margin: 0;
  transform: translateY(-50%);
}
.checkmark {
  position: absolute;
  top: 50%;
  left: 0;
  box-sizing: border-box;
  width: 24px;
  height: 24px;
  transform: translateY(-50%);
  pointer-events: none;
}
.checkmark:after {
  content: "";
  position: absolute;
  display: none;
}
.radio-button input:checked ~ .checkmark:after {
  display: block;
}
.radio-button .checkmark:after {
  top: 50%;
  left: 50%;
  width: 8px;
  height: 8px;
  background: white;
  position: absolute;
  transform: translate(-50%, -50%);
}
.radio-group-label {
  display: flex;
  align-items: center;
  font-size: inherit;
}
.radio-group-icon {
  width: 16px;
  height: 16px;
  margin-right: 4px;
}
</style>
